import { IUser } from '../Interfaces/IUser';
import { IUserGroup } from '../Interfaces/IUserGroup';
import { ILegalRoleTemplate } from '../Interfaces/ILegalRoleTemplate';

/**
 * Sucht in einer User-Liste den Treffer mit dem aehnlichsten Namen.
 * Vergleicht "Vorname Nachname" tokenisiert und case-/diakritik-tolerant.
 */

const _normalize = (s: string): string =>
  s
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .trim();

const _tokenize = (s: string | undefined | null): Set<string> => {
  if (!s) return new Set();
  return new Set(
    _normalize(s)
      .replace(/[,.;()\-_/]/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
  );
};

const _score = (hint: string | null | undefined, candidate: string): number => {
  if (!hint || !candidate) return 0;
  const h = _normalize(hint);
  const c = _normalize(candidate);
  if (h === c) return 1000;
  const ht = _tokenize(hint);
  const ct = _tokenize(candidate);
  if (ht.size === 0 || ct.size === 0) return 0;
  let matches = 0;
  ht.forEach(t => {
    if (ct.has(t)) matches++;
  });
  if (matches === 0) return 0;
  const coverage = matches / ht.size;
  let score = coverage * 70;
  if (c.includes(h) || h.includes(c)) score += 30;
  return score;
};

const _fullName = (u: IUser): string =>
  `${u.FirstName ?? ''} ${u.LastName ?? ''}`.trim();

export const findUserByName = (
  hint: string | null | undefined,
  users: IUser[] | undefined,
  min = 25
): IUser | null => {
  if (!hint || !users || users.length === 0) return null;
  let best: IUser | null = null;
  let bestScore = 0;
  for (const u of users) {
    if (u.isBlocked) continue;
    const s = _score(hint, _fullName(u));
    if (s > bestScore) {
      bestScore = s;
      best = u;
    }
  }
  return bestScore >= min ? best : null;
};

export const findUserGroupByName = (
  hint: string | null | undefined,
  groups: IUserGroup[] | undefined,
  min = 25
): IUserGroup | null => {
  if (!hint || !groups || groups.length === 0) return null;
  let best: IUserGroup | null = null;
  let bestScore = 0;
  for (const g of groups) {
    const s = _score(hint, g.UserGroup ?? '');
    if (s > bestScore) {
      bestScore = s;
      best = g;
    }
  }
  return bestScore >= min ? best : null;
};

/**
 * Sucht in den LegalRoleTemplates der Zielvorgangsart den besten Treffer
 * auf den Quell-Rollennamen (z.B. "Verkäufer" → Template mit gleicher
 * Bezeichnung). Tolerant gegen Umlaute, Pluralformen, Klammer-Zusaetze.
 */
export const findLegalRoleTemplateByName = (
  hint: string | null | undefined,
  templates: ILegalRoleTemplate[] | undefined,
  min = 25
): ILegalRoleTemplate | null => {
  if (!hint || !templates || templates.length === 0) return null;
  let best: ILegalRoleTemplate | null = null;
  let bestScore = 0;
  for (const t of templates) {
    const s = _score(hint, t.LegalRoleTemplate ?? '');
    if (s > bestScore) {
      bestScore = s;
      best = t;
    }
  }
  return bestScore >= min ? best : null;
};
